import { useQuery } from '@apollo/client';
import { Box, Button, ButtonSize, Heading, Icon, IconSize, ModalBody, Text } from '@bechtel/gds';
import {
  MetadataTableName,
  SearchInput,
  SearchTermViewResult,
  SortType,
  TermView,
  UiTextEnum,
} from '@bechtel/sr-types';
import { ColDef } from 'ag-grid-community';
import { concat, isNil, sortBy, uniqBy } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { ClientGrid, GridSkeleton, MultiLingualText, useLanguage } from '~/components';
import { VIEW_TERMS } from '~/gql';
import { FooterButtonStyled, ModalCloseStyled, ModalFooterStyled, ModalHeaderStyled, ModalStyled } from './styled';

export interface ViewTermProps {
  equipmentTag: string;
  blockTag: string;
  pointTag: string;
}

interface ViewTermModalProps {
  viewTerms: ViewTermProps;
  onClose: () => void;
}

export const ViewTermModal: FC<ViewTermModalProps> = ({ viewTerms, onClose }) => {
  const { equipmentTag, blockTag, pointTag } = viewTerms;
  const [viewTermData, setViewTermData] = useState<TermView[]>([]);
  const { getMultiLingualTextByKey } = useLanguage();
  // search from side term data
  const {
    data: fromData,
    loading: fromLoading,
    error: fromError,
  } = useQuery<{
    searchInput: SearchInput;
    searchTermView: SearchTermViewResult;
  }>(VIEW_TERMS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              field: 'fromEquipmentTag',
              operator: 'EQUAL',
              value: equipmentTag,
            },
            {
              field: 'fromBlockTag',
              operator: 'EQUAL',
              value: blockTag,
            },
            {
              field: 'fromPointTag',
              operator: 'EQUAL',
              value: pointTag,
            },
          ],
        },
        sortInput: [
          { field: 'isCableOrJumper', sort: SortType.ASC },
          { field: 'tag', sort: SortType.ASC },
          { field: 'sequenceNumber', sort: SortType.ASC },
        ],
      },
    },
  });
  // search to side term data
  const {
    data: toData,
    loading: toLoading,
    error: toError,
  } = useQuery<{
    searchInput: SearchInput;
    searchTermView: SearchTermViewResult;
  }>(VIEW_TERMS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              field: 'toEquipmentTag',
              operator: 'EQUAL',
              value: equipmentTag,
            },
            {
              field: 'toBlockTag',
              operator: 'EQUAL',
              value: blockTag,
            },
            {
              field: 'toPointTag',
              operator: 'EQUAL',
              value: pointTag,
            },
          ],
        },
        sortInput: [
          { field: 'isCableOrJumper', sort: SortType.ASC },
          { field: 'tag', sort: SortType.ASC },
          { field: 'sequenceNumber', sort: SortType.ASC },
        ],
      },
    },
  });

  useEffect(() => {
    // Once fromData && toData is populated concate and sort
    if (
      fromData &&
      fromData.searchTermView.items &&
      fromData.searchTermView.items.length > 0 &&
      toData &&
      toData.searchTermView.items &&
      toData.searchTermView.items.length > 0
    ) {
      const result = sortBy(
        uniqBy(concat(fromData.searchTermView.items, toData.searchTermView.items), 'id'),
        (term: TermView) => {
          const cableJumper = isNil(term.isCableOrJumper) ? 'C' : term.isCableOrJumper;
          const tag = isNil(term.tag) ? '' : term.tag;
          const sequenceNumber = isNil(term.sequenceNumber) ? '0' : term.sequenceNumber?.toString();
          return cableJumper.concat(tag).concat(sequenceNumber);
        }
      );
      setViewTermData(result);
    } else if (
      fromData &&
      fromData.searchTermView.items &&
      fromData.searchTermView.items.length > 0 &&
      (!toData || !toData.searchTermView.items || !toData.searchTermView.items.length)
    ) {
      // sort happens in the server
      setViewTermData(fromData.searchTermView.items);
    } else if (
      toData &&
      toData.searchTermView.items &&
      toData.searchTermView.items.length > 0 &&
      (!fromData || !fromData.searchTermView.items || !fromData.searchTermView.items.length)
    ) {
      // sort happens in the server
      setViewTermData(toData.searchTermView.items);
    }
  }, [fromData, toData]);

  const defaultColDef: ColDef = useMemo(() => {
    return {
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  return (
    <>
      <ModalStyled visible>
        <>
          <ModalHeaderStyled title="">
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-evenly' }}>
              <Box pl={{ sm: '300' }}>
                <Heading size="500" weight="500">
                  <Text>
                    {getMultiLingualTextByKey(UiTextEnum.WIRES_TERMINATED_ON_VALUE, [
                      `${equipmentTag}, ${blockTag}, ${pointTag}`,
                    ])}
                  </Text>
                </Heading>
              </Box>
              <ModalCloseStyled
                onClick={() => {
                  onClose();
                }}
              >
                <Button
                  size={ButtonSize.SMALL}
                  style={{ background: 'transparent', boxShadow: 'none' }}
                  onClick={() => close()}
                >
                  <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
                </Button>
              </ModalCloseStyled>
            </Box>
          </ModalHeaderStyled>
          {/* @ts-ignore adjust in GDS component to take child as prop  */}
          <ModalBody
            css={`
              flex-direction: column;
            `}
          >
            {(fromLoading || toLoading) && <GridSkeleton />}
            {fromError && <>{fromError.message}</>}
            {toError && <>{toError.message}</>}
            {!(fromLoading || toLoading || fromError || toError) && (
              <Box mt={{ sm: '450' }} flexGrow={{ sm: 1 }}>
                <ClientGrid
                  originalRowData={viewTermData}
                  name={MetadataTableName.TERM_VIEW}
                  rowSelection="single"
                  hideLayoutHeader={true}
                  canCreateLayout={false}
                  systemLayoutName={'termView'}
                  rowData={viewTermData}
                  disableNewRow={true}
                  skipPermission={true}
                  hideFloatingFilter={true}
                  readOnly={true}
                  customDefaultColDef={defaultColDef}
                  hideCheckBoxColumn={true}
                ></ClientGrid>
              </Box>
            )}
          </ModalBody>
          <ModalFooterStyled>
            <Box p={{ sm: '500' }} pt={{ sm: '200' }}>
              <FooterButtonStyled onClick={onClose}>
                <Box px={{ sm: '500' }}>
                  <MultiLingualText>Cancel</MultiLingualText>
                </Box>
              </FooterButtonStyled>
            </Box>
          </ModalFooterStyled>
        </>
      </ModalStyled>
    </>
  );
};
